"use client";
import Image from "next/image";
import { useRef } from "react";
import { academicFeatures } from "@data";
import { academicSolotions } from "@assets";
import { motion, useInView } from "framer-motion";
import { Card, CardContent } from "@components/ui/card";

const AcademicSolutions = () => {
	const titleRef = useRef(null);
	const isTitleInView = useInView(titleRef, { once: true, margin: "-100px" });

	return (
		<section className="relative flex w-full items-center justify-center overflow-hidden px-4 sm:px-6 lg:px-8">
			<div className="absolute inset-0">
				<Image alt="academic-solutions" className="size-full object-cover opacity-10" priority src={academicSolotions} />
			</div>
			<div className="relative mx-auto flex w-full max-w-7xl items-center justify-center py-12">
				<div className="flex w-full flex-col items-center gap-10 px-2 text-center sm:px-4">
					<motion.div animate={{ opacity: 1, scale: 1 }} className="bg-secondary mx-auto h-6 w-24 rounded-full sm:h-8 sm:w-32" initial={{ opacity: 0, scale: 0.8 }} transition={{ duration: 0.6 }} />
					<motion.h1 animate={isTitleInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }} className="text-foreground px-2 text-2xl leading-tight font-bold sm:text-3xl md:text-4xl lg:text-4xl xl:text-4xl" initial={{ opacity: 0, y: 30 }} ref={titleRef} transition={{ duration: 0.8 }}>
						{"Smarter Learning for Schools & Universities"}
					</motion.h1>
					<motion.p animate={isTitleInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} className="text-muted-foreground mx-auto max-w-2xl px-2 text-sm sm:text-base md:text-lg" initial={{ opacity: 0, y: 20 }} transition={{ duration: 0.6, delay: 0.3 }}>
						{"Transform your educational institution with our comprehensive digital learning solutions designed for modern education."}
					</motion.p>
					<div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 lg:flex-nowrap lg:gap-8">
						{academicFeatures?.map((feature, index) => {
							const Icon = feature?.icon;
							const ref = useRef(null);
							const isInView = useInView(ref, { once: true, margin: "-100px" });

							return (
								<motion.div animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }} initial={{ opacity: 0, y: 50 }} key={feature?.uuid} ref={ref} transition={{ duration: 0.6, delay: index * 0.2 }}>
									<Card className="bg-background/10 border-0 shadow-lg backdrop-blur-xl transition-all duration-300 hover:scale-105 hover:shadow-xl">
										<CardContent className="p-4 text-center sm:p-6">
											<motion.div className="bg-secondary/20 mx-auto flex size-12 items-center justify-center rounded-full sm:size-16" transition={{ duration: 0.3 }} whileHover={{ scale: 1.1, rotate: 5 }}>
												<Icon className="stroke-secondary" size={32} />
											</motion.div>
											<h3 className="text-foreground text-base font-bold sm:text-lg">{feature?.title}</h3>
											<p className="text-muted-foreground text-xs leading-relaxed sm:text-sm">{feature?.description}</p>
										</CardContent>
									</Card>
								</motion.div>
							);
						})}
					</div>
				</div>
			</div>
		</section>
	);
};

export default AcademicSolutions;
